import React, { useContext, useEffect, useState } from 'react'
import Icon from '@embracesbs/component-icon'
import Modal from '@embracesbs/component-modal'
import Text from '@embracesbs/component-text'
import TextField from '@embracesbs/component-textfield'
import TextFieldWrapper from '@embracesbs/component-textfieldwrapper/TextFieldWrapper'
import { AuthRoleContext } from '../Auth/AuthRoleContext'
import { useSystemStatus } from '../Context/SystemStatusContext'
import { useApi } from '../../Hooks'
import { displayName, initials, avatarHue } from '../../Utilities/userDisplay'

const ACTIVE_USERS_REFRESH_MS = 60000

interface ActiveUser {
  userName: string
  lastSeenUtc: string
}

const itemStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '2px',
  padding: '12px 0',
  cursor: 'pointer'
}

// lastSeenUtc is UTC, but EF reads the datetime2 column back as a
// Kind=Unspecified DateTime, so it serialises without a trailing 'Z'. A bare
// timestamp is parsed by `new Date` as LOCAL time, which put every last-seen
// ~2h off in CEST — normalise to UTC before parsing. (WI 165958)
const parseUtc = (iso: string): number => {
  const hasZone = /[Zz]$|[+-]\d{2}:?\d{2}$/.test(iso)
  return new Date(hasZone ? iso : `${iso}Z`).getTime()
}

const relativeTime = (iso: string): string => {
  const secs = Math.max(0, Math.floor((Date.now() - parseUtc(iso)) / 1000))
  if (secs < 60) return 'just now'
  const mins = Math.floor(secs / 60)
  return `${mins} min ago`
}

// Admin-only sidebar tools: who-is-online counter and the maintenance switch.
const SidebarAdminTools: React.FC = () => {
  const roles = useContext(AuthRoleContext)
  const { maintenance, refreshSystemStatus } = useSystemStatus()
  const { fetchApi } = useApi()

  const [activeUsers, setActiveUsers] = useState<ActiveUser[]>([])
  const [isUsersModalOpen, setIsUsersModalOpen] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [message, setMessage] = useState('')
  const [isSaving, setIsSaving] = useState(false)

  const isAdmin = !!roles?.isAdmin

  useEffect(() => {
    if (!isAdmin) return
    const load = async () => {
      try {
        const response = await fetchApi({ endpoint: '/api/presence/active', method: 'GET' })
        setActiveUsers(Array.isArray(response?.users) ? response.users : [])
      } catch (error) {
        console.debug('Failed to fetch active users', error)
      }
    }
    load()
    const interval = setInterval(load, ACTIVE_USERS_REFRESH_MS)
    return () => clearInterval(interval)
  }, [isAdmin, fetchApi])

  if (!isAdmin) return null

  const toggleMaintenance = async () => {
    setIsSaving(true)
    try {
      await fetchApi({
        endpoint: '/api/presence/maintenance',
        method: 'PUT',
        body: { enabled: !maintenance.enabled, message: message.trim() || undefined }
      })
      refreshSystemStatus()
      setIsModalOpen(false)
      setMessage('')
    } catch (error) {
      console.error('Failed to toggle maintenance mode', error)
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <>
      {/* Online users — click to see who's online (no more giant tooltip). */}
      <div
        style={itemStyle}
        onClick={() => setIsUsersModalOpen(true)}
        data-tooltip-id='global-tooltip'
        data-tooltip-content='Online users — click for details'
      >
        <Icon iconName='SingleNeutral' size={20} color='var(--tma-band-text-muted)' />
        <span style={{ fontSize: '10px', fontWeight: 600, color: 'var(--tma-band-text-muted)', fontFamily: 'Inter, sans-serif' }}>
          {activeUsers.length}
        </span>
      </div>

      {/* Maintenance toggle */}
      <div
        style={itemStyle}
        onClick={() => setIsModalOpen(true)}
        data-tooltip-id='global-tooltip'
        data-tooltip-content={maintenance.enabled ? 'Maintenance mode is ON — click to manage' : 'Enable maintenance mode'}
      >
        <Icon iconName='Wrench' size={20} color={maintenance.enabled ? '#e74c3c' : 'var(--tma-band-text-muted)'} />
      </div>

      {/* Online users dialog */}
      <Modal
        isOpen={isUsersModalOpen}
        width='40%'
        height='auto'
        onClose={() => setIsUsersModalOpen(false)}
        closeOnOverlayClick
        closeOnEsc
        showCloseIcon
        hasHeaderBorder={true}
        headerTitle={`Online users (${activeUsers.length})`}
      >
        <div style={{ maxHeight: '55vh', overflowY: 'auto' }} className='custom-scrollbar'>
          {activeUsers.length === 0 ? (
            <Text
              textStyle='description'
              textColor='grey'
              content='No one active in the last 5 minutes.'
              style={{ display: 'block', padding: '8px 4px' }}
            />
          ) : (
            activeUsers
              .slice()
              .sort((a, b) => displayName(a.userName).localeCompare(displayName(b.userName)))
              .map((user) => (
                <div
                  key={user.userName}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '10px 4px',
                    borderBottom: '1px solid var(--tma-card-border)'
                  }}
                >
                  <div
                    style={{
                      width: '34px',
                      height: '34px',
                      borderRadius: '50%',
                      flexShrink: 0,
                      background: `hsl(${avatarHue(user.userName)}, 45%, 55%)`,
                      color: '#ffffff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '13px',
                      fontWeight: 700
                    }}
                  >
                    {initials(user.userName)}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <Text textStyle='body' content={displayName(user.userName)} singleLine style={{ fontWeight: 600 }} />
                    <Text textStyle='description' textColor='grey' content={user.userName} singleLine hasTooltipEllipsis />
                  </div>
                  <span
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      color: 'var(--tma-success-text)',
                      fontSize: '12px',
                      flexShrink: 0
                    }}
                  >
                    <span
                      style={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        background: 'var(--tma-success-text)',
                        display: 'inline-block'
                      }}
                    />
                    {relativeTime(user.lastSeenUtc)}
                  </span>
                </div>
              ))
          )}
        </div>
      </Modal>

      <Modal
        isOpen={isModalOpen}
        width='45%'
        onClose={() => setIsModalOpen(false)}
        onCancel={() => setIsModalOpen(false)}
        hasHeaderBorder={true}
        headerTitle={maintenance.enabled ? 'Disable maintenance mode' : 'Enable maintenance mode'}
        height='auto'
        onConfirm={toggleMaintenance}
        isConfirmDisabled={isSaving}
        variant='confirm'
        labelConfirm={maintenance.enabled ? 'Disable' : 'Enable'}
        labelCancel='Cancel'
      >
        {maintenance.enabled ? (
          <Text>
            Maintenance mode is currently <strong>enabled</strong>
            {maintenance.updatedBy ? ` (by ${maintenance.updatedBy})` : ''}. Disabling it re-enables tenant actions for all users.
          </Text>
        ) : (
          <>
            <Text style={{ marginBottom: '12px', display: 'block' }}>
              All open frontends will show a maintenance banner and tenant actions will be disabled for non-admin users until you switch it
              off.
            </Text>
            <TextFieldWrapper labelText='Message (optional)'>
              <TextField
                placeholder='e.g. Maintenance until 15:00 — tenant updates are disabled'
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onClear={() => setMessage('')}
                type='text'
                variant='default'
              />
            </TextFieldWrapper>
          </>
        )}
      </Modal>
    </>
  )
}

export default SidebarAdminTools
